import {NavigationFailure, RouteLocationNormalized, RouteLocationRaw, Router, START_LOCATION} from 'vue-router'
import {RouteName} from '@toolify/client/src/setup/router/enum/RouteName'
import {useResponsive} from '@toolify/client/src/composables/useResponsive'
import {prefetchDatabaseItemPages} from '@toolify/client/src/modules/databases/functions/prefetchDatabaseItemPages'
import {getDatabaseItemRouteSource} from '@toolify/client/src/modules/databases/functions/getDatabaseItemRouteSource'
import {getDatabaseItemRouteSourceQuery} from '@toolify/client/src/modules/databases/functions/getDatabaseItemRouteSourceQuery'

// The database page an item address opens under: the view named in the address, or the database's own page, which
// sends the user on to the view they last had open there.
export function getDatabaseItemBaseLocation(itemRoute: Pick<RouteLocationNormalized, 'params' | 'query'>): RouteLocationRaw {
  const {databaseId, viewId} = getDatabaseItemRouteSource(itemRoute)
  return {
    name: viewId ? RouteName.DatabaseView : RouteName.Database,
    params: {
      workspaceName: itemRoute.params.workspaceName,
      databaseId,
      ...(viewId ? {databaseViewId: viewId} : {}),
    },
    replace: true,
  }
}

// An item address opened on its own (a reload, a link in a new tab or from an email) first opens its database page
// and then the item over it, as if it were opened from that page: in the user's layout, with X and Back leading to
// the view. Phones keep the full item page they open such an address in.
export function openDatabaseItemOverItsViewRouterHooksFactory(
  router: Router,
  prefetchItemPages: () => void = prefetchDatabaseItemPages,
) {
  let pendingItemRoute: RouteLocationNormalized | null = null

  function beforeEach(to: RouteLocationNormalized, from: RouteLocationNormalized) {
    if(from !== START_LOCATION || to.name !== RouteName.DatabaseItem || useResponsive().isSmallerThanLarge.value) {
      return
    }
    pendingItemRoute = to
    // The item's own code downloads alongside the database page instead of after it.
    prefetchItemPages()
    return getDatabaseItemBaseLocation(to)
  }

  function afterEach(to: RouteLocationNormalized, from: RouteLocationNormalized, failure?: NavigationFailure | void) {
    const itemRoute = pendingItemRoute
    pendingItemRoute = null
    if(!itemRoute || from !== START_LOCATION || failure) {
      return
    }
    const source = getDatabaseItemRouteSource(itemRoute)
    if(to.params.databaseId !== source.databaseId) {
      return
    }
    // A database without a view the user can open stays on its own page; the item still opens over it.
    if(to.name !== RouteName.DatabaseView && to.name !== RouteName.Database) {
      return
    }
    const viewId = to.name === RouteName.DatabaseView ? to.params.databaseViewId as string : null
    void router.push({
      name: RouteName.DatabaseItem,
      params: itemRoute.params,
      query: viewId ? {
        ...itemRoute.query,
        ...getDatabaseItemRouteSourceQuery({
          ...source,
          viewId,
        }),
      } : itemRoute.query,
      hash: itemRoute.hash,
    })
  }

  return {
    beforeEach,
    afterEach,
  }
}
